import { useState } from "react";
import { DayPicker } from "react-day-picker";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { ReactComponent as CalendarIcon } from "../../../assets/icons/calendar-fold.svg";
import { ReactComponent as CalendarArrowDownIcon } from "../../../assets/icons/calendar-arrow-down.svg";
import { ReactComponent as CalendarArrowUpIcon } from "../../../assets/icons/calendar-arrow-up.svg";
import "react-day-picker/dist/style.css";
import styles from "./DateRangePicker.module.css";

/**
 * Componente DateRangePicker
 * Selector de rango de fechas usando react-day-picker
 *
 * @param {Object} props
 * @param {Object} props.value - Rango de fechas seleccionado { from: Date, to: Date }
 * @param {function} props.onChange - Función callback cuando cambia el rango
 * @param {Date} props.minDate - Fecha mínima seleccionable
 * @param {number} props.minNights - Número mínimo de noches
 * @param {string} props.error - Mensaje de error
 * @param {boolean} props.darkMode - Modo oscuro
 */
const DateRangePicker = ({
  value,
  onChange,
  minDate = new Date(),
  minNights = 1,
  error,
  darkMode = false,
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [hoveredDay, setHoveredDay] = useState(null);

  const handleSelect = (range) => {
    onChange(range);
    // No cerrar automáticamente el calendario para permitir modificaciones
  };

  const handleDayMouseEnter = (day) => {
    // Mostrar hover preview siempre que haya una fecha seleccionada
    if (value?.from) {
      setHoveredDay(day);
    }
  };

  const handleDayMouseLeave = () => {
    setHoveredDay(null);
  };

  const formatDisplayDate = (date) => {
    if (!date) return "Seleccionar";
    return format(date, "d 'de' MMM yyyy", { locale: es });
  };

  // Crear modifiers para el hover preview
  const modifiers = {
    hoverRange: (day) => {
      if (!hoveredDay || !value?.from) return false;
      
      // Si no hay 'to' seleccionado, mostrar preview desde 'from' hasta hover
      if (!value?.to) {
        const isAfterFrom = day > value.from;
        const isBeforeHovered = day < hoveredDay;
        return isAfterFrom && isBeforeHovered;
      }
      
      // Si ya hay un rango completo, mostrar preview del nuevo rango potencial
      if (hoveredDay < value.from) {
        // Hover está antes del from actual
        const isAfterHovered = day > hoveredDay;
        const isBeforeTo = day < value.to;
        return isAfterHovered && isBeforeTo;
      } else if (hoveredDay > value.to) {
        // Hover está después del to actual
        const isAfterFrom = day > value.from;
        const isBeforeHovered = day < hoveredDay;
        return isAfterFrom && isBeforeHovered;
      }
      
      return false;
    },
    hoverRangeStart: hoveredDay && value?.from ? (
      !value?.to ? value.from : 
      hoveredDay < value.from ? hoveredDay : value.from
    ) : undefined,
    hoverRangeEnd: hoveredDay && value?.from ? (
      !value?.to ? hoveredDay :
      hoveredDay > value.to ? hoveredDay : value.to
    ) : undefined,
  };

  return (
    <div
      className={`${styles["date-picker-container"]} ${
        darkMode ? styles.dark : ""
      }`}
    >
      {/* Label */}
      <div className={styles["date-picker-label"]}>
  <CalendarIcon className={styles["date-picker-icon"]} />
  <h3 className={styles["date-picker-label-text"]}>Fechas de Viaje</h3>
</div>


      {/* Inputs de fecha */}
      <div className={styles["date-inputs-grid"]}>
        {/* Check-in */}
        <div className={styles["date-input-wrapper"]}>
          <label className={styles["date-input-label"]}>Llegada</label>
          <div className={styles["date-input-field"]}>
            <span className={styles["date-input-icon"]}>
              <CalendarArrowUpIcon />
            </span>
            <input
              type="text"
              className={styles["date-input"]}
              value={formatDisplayDate(value?.from)}
              onClick={() => setShowCalendar(!showCalendar)}
              readOnly
              placeholder="Seleccionar"
            />
          </div>
        </div>

        {/* Check-out */}
        <div className={styles["date-input-wrapper"]}>
          <label className={styles["date-input-label"]}>Salida</label>
          <div className={styles["date-input-field"]}>
            <span className={styles["date-input-icon"]}>
              <CalendarArrowDownIcon />
            </span>
            <input
              type="text"
              className={styles["date-input"]}
              value={formatDisplayDate(value?.to)}
              onClick={() => setShowCalendar(!showCalendar)}
              readOnly
              placeholder="Seleccionar"
            />
          </div>
        </div>
      </div>

      {/* Hint o Error */}
      {error ? (
        <p className={styles["date-picker-error"]}>{error}</p>
      ) : (
        <p className={styles["date-picker-hint"]}>
          Estancia mínima requerida: {minNights}{" "}
          {minNights === 1 ? "noche" : "noches"}
        </p>
      )}

      {/* Calendario */}
      {showCalendar && (
        <DayPicker
          mode="range"
          selected={value}
          onSelect={handleSelect}
          onDayMouseEnter={handleDayMouseEnter}
          onDayMouseLeave={handleDayMouseLeave}
          disabled={{ before: minDate }}
          locale={es}
          numberOfMonths={1}
          className={styles.rdp}
          modifiers={modifiers}
          modifiersClassNames={{
            hoverRange: styles["rdp-day_hover_range"],
            hoverRangeStart: styles["rdp-day_hover_range_start"],
            hoverRangeEnd: styles["rdp-day_hover_range_end"],
          }}
        />
      )}
    </div>
  );
};

export default DateRangePicker;
